import { getNamedRelatedInstance } from '@iotv/core-services'
import {
  AdjacentType,
  ComparisonType,
  LogicalOperatorType,
  ValidBaseGroupTypes,
  ValidBusinessObject,
  ValidBusinessObjectList,
  getZombieEdge,
  getZombieInstanceFromPrimaryKey,
  isNode,
} from '@iotv/iotv-v3-types'
import { edgeTypeMatrix } from '../classDependentTypes/EdgeTypes/EdgeTypeMatrix'
import { NodeEdgeMapper } from '../model/constructs'
import { getChildEdgeTypeId, getParentEdgeTypeId } from '../classDependentTypes'
import { DEBUG_OVERRIDE, NOT_LAMBDA } from '../util/lambda/debug'

const debug = DEBUG_OVERRIDE || (NOT_LAMBDA && false)

export const generateDefaultGroupForCustomer = <T extends ValidBaseGroupTypes>(customer: ValidBusinessObject, groupType: T, includeCustomerName?: boolean) => {
  const defaultName = `Default ${groupType}`
  const name = includeCustomerName ? `${customer.name} ${defaultName}` : defaultName
  const group = getNamedRelatedInstance({ type: 'Group', name: defaultName, parentUuid: customer.id, others: { supportedType: groupType } })
  /** use extended name to avoid confusion over mutiple groups with same name */
  group.name = name
  const customerToGroupEdge = getZombieEdge(customer, group, edgeTypeMatrix.Customer.Group.customer_has_group.edgeTypeId)
  return [customerToGroupEdge, group]
}

/**
 * Generate an edge between an item and group, where the group is the parent
 * @param item entity
 * @param group group that supports entity type
 * @returns
 */
export const generateGroupContainedItemToGroupsObjects = (item: ValidBusinessObject, group: ValidBusinessObject) => {
  const res: ValidBusinessObjectList = []
  if (item.type === group.supportedType) {
    const edge = getZombieEdge(group, item, getChildEdgeTypeId('Group', item.type))
    res.push(item, edge)
  }
  return res
}

/** utility used in context construction ( eg TestData ) taking a nodeEdgeMapper with a single customer and mulitple groupableItems, returning a structure with a group for each type, with edge to customer and containing groupableTypes
 * potentially this can be used as part of an iteration on multiple customer nodeEdgeMapper using extractSingleNodeEdgeMapper
 *
 * groupTypes should have enitity types added from classDerivedDeviceEnabledTypes ( @iotv/datamodel ) in concrete usages
 */
export const normalizeCustomerNodeEdgeMapper = <T extends ValidBaseGroupTypes>(
  nem: NodeEdgeMapper<ValidBusinessObject>,
  groupTypes: T[] = ['Customer', 'Device', 'Gateway', 'User'] as T[],
) => {
  const customers = nem.getNodesOfType('Customer')
  if (customers.length === 1) {
    customers.forEach((customer) => {
      groupTypes.forEach((groupType) => {
        let targetGroup: ValidBusinessObject | undefined = undefined
        const instancesOfGroupType = nem.getNodesOfType(groupType).filter((item) => item.sk !== customer.sk)
        if (instancesOfGroupType.length > 0) {
          const existingCustomerGroups = nem.getAdjacentNodes(customer, {
            objectTypeId: 'Group',
            adjacencyType: AdjacentType.CHILD,
            edgeTypeId: getChildEdgeTypeId('Group', groupType),
            nodeFilters: [{ setOperation: LogicalOperatorType.AND, filters: [{ key: 'supportedType', predicate: ComparisonType.EQUALS, value: groupType }] }],
          })
          if (existingCustomerGroups.length === 0) {
            const loadableItems = generateDefaultGroupForCustomer(customer, groupType, true)
            targetGroup = loadableItems.find(isNode)
            nem.loadItems(loadableItems)
          } else if (existingCustomerGroups.length === 1) {
            targetGroup = existingCustomerGroups.at(0)
          }
          if (targetGroup) {
            instancesOfGroupType.forEach((item) => {
              const loadableItems = generateGroupContainedItemToGroupsObjects(item, targetGroup!) // The assertion is not needed under typescript 5.4
              nem.loadItems(loadableItems)
            })
          }
        }
      })
    })
  } else {
    debug && console.log(`Not operating as customers length is ${customers.length}`)
  }

  return nem
}
